import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    NgZone
} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {HubspotFormsService} from './hubspot-forms.service';

type EmbedState = 'loading' | 'ready' | 'error' | 'disabled';

interface HubspotFormCreateOptions {
    portalId: string;
    formId: string;
    region: string;
    target: string;
    onFormReady?: () => void;
}

@Component({
    selector: 'app-hubspot-form',
    standalone: true,
    templateUrl: './hubspot-form.component.html',
    styleUrls: ['./hubspot-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HubspotFormComponent implements AfterViewInit {
    @Input({required: true}) portalId!: string;
    @Input({required: true}) formId!: string;
    @Input() region = 'eu1';

    readonly targetId = `hubspot-form-${Math.random().toString(36).slice(2)}`;
    state: EmbedState = 'loading';
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly ngZone = inject(NgZone);
    private readonly hubspotForms = inject(HubspotFormsService);
    private hasRendered = false;

    async ngAfterViewInit(): Promise<void> {
        const win = this.document.defaultView as Window | null;
        if (!win || typeof window === 'undefined' || win.__karma__) {
            this.state = 'disabled';
            this.cdr.markForCheck();
            return;
        }

        try {
            await this.hubspotForms.ensureEmbedScript(this.region);
        } catch {
            this.state = 'error';
            this.cdr.markForCheck();
            return;
        }

        if (this.hasRendered) {
            return;
        }

        const hbspt = (win as Window).hbspt;
        const forms = hbspt?.forms;
        if (!forms?.create) {
            this.state = 'error';
            this.cdr.markForCheck();
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            const options: HubspotFormCreateOptions = {
                portalId: this.portalId,
                formId: this.formId,
                region: this.region,
                target: `#${this.targetId}`,
                onFormReady: () => {
                    this.ngZone.run(() => {
                        this.state = 'ready';
                        this.cdr.markForCheck();
                    });
                },
            };

            try {
                forms.create(options as unknown as Record<string, unknown>);
            } catch {
                this.ngZone.run(() => {
                    this.state = 'error';
                    this.cdr.markForCheck();
                });
            }
        });

        this.hasRendered = true;
    }
}
